/**
 * @file Vignette d'arme partagee (coffre, emplacements, collection, revelation de tirage) : pastille aux couleurs du grade, silhouette, comportement.
 */
import type { WeaponDef } from '@config/types';
import { h } from '../../core/dom';
import { BEHAVIOR_ICON, weaponIcon } from '../../core/icons';
import { uiIcon } from '../../core/uiIcons';
import type { UiContext } from '../../UiContext';

export interface TileParts {
  root: HTMLElement;
  slot: HTMLElement;
  lock: HTMLElement;
  merge: HTMLElement;
}

/** Variables CSS de couleur du grade (--g, --g2, --glow) appliquees sur un element. */
export const paintGrade = (ctx: UiContext, el: HTMLElement, def: WeaponDef): void => {
  const g = ctx.index.grade(def.grade);
  el.style.setProperty('--g', g.colors.primary);
  el.style.setProperty('--g2', g.colors.secondary);
  el.style.setProperty('--glow', g.colors.glow);
  el.style.setProperty('--glow-a', g.glow.toFixed(2));
  el.dataset.grade = def.grade;
  el.classList.toggle('shimmer', g.shimmer);
};

/** Cree une vignette (sans etat de coffre : verrou / slot / fusion sont masques et pilotes par l'appelant). */
export const createTile = (ctx: UiContext, def: WeaponDef, extraClass = ''): TileParts => {
  const g = ctx.index.grade(def.grade);
  const slot = h('span', { class: 'tile-slot is-hidden' });
  const lock = h('span', { class: 'tile-lock is-hidden' }, uiIcon('lock'));
  const merge = h('span', { class: 'tile-merge is-hidden', title: ctx.i18n.t('weapons.merge') }, uiIcon('arrowUp'));
  const root = h(
    'div',
    { class: `tile ${extraClass}`.trim(), title: ctx.i18n.name(def.name), role: 'button', tabindex: 0 },
    h('span', { class: 'tile-grade' }, def.grade),
    weaponIcon(def, g),
    h('span', { class: 'tile-beh' }, uiIcon(BEHAVIOR_ICON[def.behavior])),
    slot,
    lock,
    merge,
  );
  paintGrade(ctx, root, def);
  return { root, slot, lock, merge };
};
